/** The reschedule dialog's live preview: what moving the Visit's work to the picked date would do. */
import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, input, ViewEncapsulation } from '@angular/core';
import { DesignSystem } from '@workbuddy/ui/v2/design-system';
import { CustomSectionRendererInputs, FieldMode } from '@workbuddy/ui/v2/surface.engine';
import { VisitMovePreviewSummary } from '../../presentation/visit.move.preview';
import { visitMovePreviewBridgeStyles, visitMovePreviewStyleId } from './visit.move.preview.theme';

@Component({
  selector: 'wb-visit-move-preview',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  styleUrls: ['./visit.move.preview.component.less'],
  template: `
    <div class="visit-move-preview" role="status" aria-live="polite">
      @if ($stale()) {
        <p class="stale">This changed since you opened it. Check the result below and confirm again.</p>
      }
      @if ($loading()) {
        <p class="muted">Checking what this move does…</p>
      } @else if ($error(); as error) {
        <p class="error">{{ error }}</p>
      } @else if ($summary(); as summary) {
        <ul>
          @for (line of summary.outcomes; track line.text) {
            <li class="outcome" [class.warning]="line.tone === 'warning'">{{ line.text }}</li>
          }
          @for (line of summary.notes; track line.text) {
            <li [class.warning]="line.tone === 'warning'" [class.error]="line.tone === 'error'">
              @if (line.tone !== 'neutral') {
                <i class="fa-regular" [class.fa-triangle-exclamation]="line.tone === 'warning'" [class.fa-circle-xmark]="line.tone === 'error'" aria-hidden="true"></i>
              }
              <span>{{ line.text }}</span>
            </li>
          }
        </ul>
      } @else {
        <p class="muted">{{ $hint() }}</p>
      }
    </div>
  `,
})
export class VisitMovePreviewComponent implements CustomSectionRendererInputs {
  private readonly document = inject(DOCUMENT);
  public readonly $surfaceMode = input<FieldMode | undefined>(undefined, { alias: 'surfaceMode' });
  public readonly $summary = input<VisitMovePreviewSummary | null>(null, { alias: 'summary' });
  public readonly $loading = input(false, { alias: 'loading' });
  public readonly $error = input<string | null>(null, { alias: 'error' });
  /** The reviewed preview went stale on submit; the summary is the refreshed one. */
  public readonly $stale = input(false, { alias: 'stale' });
  /** Shown when there is nothing to preview yet, e.g. the date is unchanged. */
  public readonly $hint = input('', { alias: 'hint' });

  public constructor() {

    DesignSystem.Mount(this.document, visitMovePreviewStyleId, visitMovePreviewBridgeStyles);
  }
}
